import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler } from 'react-hook-form';

import { Box, Tab, TabGroup } from '@base-app';
import { COMMUNITY_OUTREACH_TRANSLATION } from '../constants/common';
import { CommunityOutreachFormValues } from '../components/CommunityOutreachForm/CommunityOutreachForm.types';
import { PageLarge, Section, SubPage } from '@builder';
import { Sizes } from '@/common/types';
import { NotificationVariants, TabContextProvider } from '@/common/components';
import { FormInquiryResponse, TabId } from './CommunityOutreachScreen.types';
import { useAddNotification } from '@/modules/notifications/store';

import CommunityOutreachForm from '../components/CommunityOutreachForm/CommunityOutreachForm';
import SuccessPage from '../components/SuccessPage/SuccessPage';
import useMutationFormInquiryForm from '../hooks/useMutationSubmitInquiryForm';
import useQueryLocationOutreach from '../hooks/useQueryOutreachLocations';

const CommunityOutreachScreen: FC = () => {
  const { t } = useTranslation(COMMUNITY_OUTREACH_TRANSLATION);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [outreachUrl, setOutreachUrl] = useState<string>('');
  const addNotification = useAddNotification();

  const handlerSuccess = async ({ enrollmentUrl }: FormInquiryResponse): Promise<void> => {
    setOutreachUrl(enrollmentUrl);
  };

  const handlerError = (error: any): void => {
    addNotification({
      title: error.response.data,
      type: NotificationVariants.ERROR,
      autoClose: false,
    });
  };

  const { mutate, isLoading } = useMutationFormInquiryForm(handlerSuccess, handlerError);
  const { data: locations, isLoading: isLoadiangLocation } = useQueryLocationOutreach();

  const resetForm = (): void => {
    setOutreachUrl('');
    setIsSubmitted(false);
  };

  const handleFormSubmit: SubmitHandler<CommunityOutreachFormValues> = (values): void => {
    mutate(values);
  };

  useEffect(() => {
    if (outreachUrl !== '') {
      setIsSubmitted(true);
    }
  }, [outreachUrl]);

  return (
    <TabContextProvider value={TabId.INQUIRY}>
      <PageLarge
        header={
          <Box
            css={{
              boxShadow: '$tabGroup',
              marginTop: '$4',
            }}
          >
            <TabGroup
              css={{
                borderBottom: 'none',
                margin: '$1 auto',
                maxWidth: '$studyInsights$containerMaxWidth',
                padding: '0 $6',
              }}
              value={TabId.INQUIRY}
              tabSize={Sizes.LARGE}
            >
              <Tab label={t('tab')} value={TabId.INQUIRY} />
            </TabGroup>
          </Box>
        }
        title={t('title_pagelarge')}
      >
        <SubPage>
          <Section title={isSubmitted ? '' : t('title_section')}>
            {isSubmitted ? (
              <SuccessPage handleResetForm={resetForm} outreachUrl={outreachUrl} />
            ) : (
              <CommunityOutreachForm
                handleFormSubmit={handleFormSubmit}
                isLoading={isLoading || isLoadiangLocation}
                locations={locations}
              />
            )}
          </Section>
        </SubPage>
      </PageLarge>
    </TabContextProvider>
  );
};

export default CommunityOutreachScreen;
